import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Globals } from 'src/app/common/globals';
import { firstValueFrom } from 'rxjs';

// ✅ Define a reusable API response type
export interface ApiResponse<T> {
    data: T;
}

// ✅ Import your existing interfaces
import {
    ActiveConsent,
    ActiveFee,
    Info,
    Voucher,
} from './payment.component';

@Injectable({
    providedIn: 'root'
})
export class PaymentService {
    constructor(private http: HttpClient, private globals: Globals) { }

    private baseUrl = this.globals.baseAPIUrl;

    // ✅ Simplified helper to convert observables to promises
    private async getRequest<T>(url: string): Promise<ApiResponse<T>> {
        return await firstValueFrom(this.http.get<ApiResponse<T>>(url));
    }

    private async postRequest<T>(url: string, body: any): Promise<ApiResponse<T>> {
        return await firstValueFrom(this.http.post<ApiResponse<T>>(url, body));
    }

    // ✅ Properly typed API methods

    getActiveConsents(application_id: string | null) {
        const url = `${this.baseUrl}/application/get-active-consents/${application_id}`;
        return this.getRequest<ActiveConsent[]>(url);
    }

    getActiveFee(application_id: string | null) {
        const url = `${this.baseUrl}/application/get-active-fees/${application_id}`;
        return this.getRequest<ActiveFee>(url);
    }

    getInfo(application_id: string | null) {
        const url = `${this.baseUrl}/application/get-application-step-info/${application_id}/payment`;
        return this.getRequest<Info>(url);
    }

    savePayment(entity: any) {
        const url = `${this.baseUrl}/application/add-update-application-payment`;
        return this.postRequest<any>(url, entity);
    }

    createCheckoutSession(entity: any) {
        const url = `${this.baseUrl}/stripe-payment/create-checkout-session`;
        return this.postRequest<any>(url, entity);
    }

    recreateCheckoutSession(entity: any) {
        const url = `${this.baseUrl}/stripe-payment/recreate-checkout-session`;
        return this.postRequest<any>(url, entity);
    }

    checkApplicationPaymentStatus(entity: any) {
        const url = `${this.baseUrl}/stripe-payment/check-application-payment-status`;
        return this.postRequest<any>(url, entity);
    }

    completePayment(entity: any) {
        const url = `${this.baseUrl}/application/complete-application-payment`;
        return this.postRequest<any>(url, entity);
    }

    failedPayment(entity: any) {
        const url = `${this.baseUrl}/application/failed-application-payment`;
        return this.postRequest<any>(url, entity);
    }

    getReceiptUrl(entity: any) {
        const url = `${this.baseUrl}/stripe-payment/get-receipt-by-intent`;
        return this.postRequest<any>(url, entity);
    }

    checkVoucher(credential_fees_id: number | null) {
        const url = `${this.baseUrl}/facility/check-voucher/${credential_fees_id}`;
        return this.getRequest<Voucher>(url);
    }
}
